import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as CookieConsent from "vanilla-cookieconsent";
import logo from "@/assets/fractional-cpo-logo.png";
import ContactModal from "@/components/new-landing/ContactModal";
import LogoCarousel from "@/components/new-landing/LogoCarousel";
import FAQSchema from "@/components/FAQSchema";
import SEO from "@/components/SEO";
import { trackEvent } from "@/utils/analytics";
const NAVY = "#002178";
const stats = [{
  number: "2.5x",
  label: "Product Conversion",
  context: "AI Enterprise Search"
}, {
  number: "5yr",
  label: "Decline Reversed",
  context: "PE-Backed Platform"
}, {
  number: "10yr",
  label: "Best Growth",
  context: "Legacy Enterprise"
}, {
  number: "2x",
  label: "Trial-to-Paid",
  context: "Consumer FinTech"
}];
const problems = [{
  number: "01",
  title: "Flat Revenue",
  description: "Failing to grow every quarter"
}, {
  number: "02",
  title: "Deal Thesis Slipping",
  description: "Product not driving growth"
}, {
  number: "03",
  title: "Board Targets Missed",
  description: "Results not materializing"
}, {
  number: "04",
  title: "Overbuilt Org",
  description: "Not delivering proportional impact"
}];
const testimonials = [{
  quote: "Love what you guys are doing since my restructure. Feedback is positive. Glad to see we're finally on track to hit our targets.",
  author: "CEO",
  company: "PE-Backed B2B Platform"
}, {
  quote: "Loved your product growth presentation, learned a lot. Can you come and present to the leaders on my team so they are aware of the great work you're doing?",
  author: "CRO",
  company: "Enterprise Technology Platform"
}, {
  quote: "You were right that there was significant value hidden in our product. I'm excited to see what we can unlock next year. Where should we look?",
  author: "CFO",
  company: "PE-Backed Enterprise Platform"
}, {
  quote: "Previously we just built and hoped the metrics would change. You guys came in and reoriented everything around specific customer journey points and showed us how we can make a bigger impact without building anything new.",
  author: "VP Engineering",
  company: "Consumer FinTech"
}, {
  quote: "It's rare to work with product leaders who understand financial planning this deeply. You've been incredibly helpful with our next year's financial projections. Your modeling of product impact changed how we plan.",
  author: "Director of Finance",
  company: "Enterprise Technology"
}];
const logos = ["Instacart", "Salesforce", "McKinsey", "Shopify", "Deloitte", "Red Hat", "Telus", "Interac", "EY", "Coveo", "Fellow", "EQ Bank"];
const slackAltTexts = [
  "Client feedback on FractionalCPO impact",
  "Slack message about product execution improvements",
  "Team feedback on product leadership",
  "Client testimonial about product strategy",
  "Partnership feedback on FractionalCPO services",
  "Lessons from working with FractionalCPO",
  "Client sharing product transformation results",
  "Testimonial about FractionalCPO services"
];
const homepageFaqs = [
  {
    question: "How much does FractionalCPO cost?",
    answer: "The question isn't what FractionalCPO costs. The question is what it costs to make the wrong leadership hire. Three quarters of no results. A board that's lost confidence. A CFO questioning every product investment. FractionalCPO eliminates that risk. We deliver results in weeks, not quarters. Contact us to discuss your situation."
  },
  {
    question: "What does FractionalCPO actually do?",
    answer: "We embed a team of senior product leaders into your organization on an interim basis. We set strategy, coach your PMs, restructure teams when needed, run experiments, and drive measurable business outcomes. We've reversed a decade of decline at a PE-backed platform and delivered proven, measurable results across every engagement."
  },
  {
    question: "How quickly can you start?",
    answer: "Most engagements start within 1-2 weeks. No recruiting process, no lengthy onboarding. Our team has done this before at companies like yours and can hit the ground running."
  },
  {
    question: "What makes FractionalCPO different from a consulting firm?",
    answer: "Consultants deliver slides and recommendations. We take interim leadership roles, make hard decisions, ship product, and are held accountable for the numbers. Our CEO client said we were 'the best thing that happened to this company in a long time.'"
  },
  {
    question: "What results has FractionalCPO delivered?",
    answer: "We reversed a decade of decline at a PE-backed platform, delivering doubled product conversion, measurable conversion improvements across all channels, and record-breaking growth. Every engagement starts with identifying the 5% of work that drives all the impact. See our case study for details."
  }
];

const LandingV2 = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [navVisible, setNavVisible] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const slackScrollRef = useRef<HTMLDivElement>(null);

  // Scroll-reveal nav: hidden at top, fades in after 100px
  useEffect(() => {
    const handleScroll = () => {
      setNavVisible(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Infinite scroll for Slack screenshots
  useEffect(() => {
    const container = slackScrollRef.current;
    if (!container) return;

    // Start in the middle of the duplicated content
    const initScroll = () => {
      const singleSetWidth = container.scrollWidth / 3;
      container.scrollLeft = singleSetWidth;
    };
    initScroll();
    const handleScroll = () => {
      const {
        scrollLeft,
        scrollWidth
      } = container;
      const singleSetWidth = scrollWidth / 3;

      // If scrolled to the end (third set), jump back to middle (second set)
      if (scrollLeft >= singleSetWidth * 2) {
        container.scrollLeft = scrollLeft - singleSetWidth;
      }
      // If scrolled to the start (first set), jump forward to middle (second set)
      if (scrollLeft <= 0) {
        container.scrollLeft = scrollLeft + singleSetWidth;
      }
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  return <div className="min-h-screen bg-[#FAFAF8] text-[#1a1a1a] overflow-x-hidden">
      <SEO
        title="Fractional CPO services | Expert product leadership"
        description="Revenue growth through product leadership. Embedded product teams for $100M-$10B companies. Turnaround, acceleration, and growth."
        path="/"
      />
      {/* Scroll-reveal Nav */}
      <nav
        className="fixed top-0 w-full z-50 bg-[#FAFAF8]/90 backdrop-blur-sm border-b border-black/5 transition-all duration-300"
        style={{
          opacity: navVisible ? 1 : 0,
          pointerEvents: navVisible ? 'auto' : 'none',
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={logo} alt="FractionalCPO" className="h-10" />
          </Link>
          <div className="flex-1 flex justify-center">
            <div className="flex items-center gap-6">
              <Link to="/assessment" className="text-sm font-semibold text-[#002178]/70 hover:text-[#002178] transition-colors tracking-wide" onClick={() => trackEvent('internal_nav', { to: '/assessment', source: 'nav_link' })}>5%</Link>
              <Link to="/pe" className="text-sm font-semibold text-[#002178]/70 hover:text-[#002178] transition-colors tracking-wide" onClick={() => trackEvent('internal_nav', { to: '/pe', source: 'nav_link' })}>PE</Link>
            </div>
          </div>
          <button onClick={() => { trackEvent('contact_click', { source: 'nav' }); setIsContactOpen(true); }} className="px-6 py-2.5 bg-[#002178] text-white text-sm font-semibold hover:scale-105 transition-transform">
            Let's chat
          </button>
        </div>
      </nav>

      {/* Hero - Massive Typography */}
      <section ref={heroRef} className="min-h-screen flex flex-col justify-center px-6 md:px-12 lg:px-24 pt-20">
        <div className="max-w-[1400px] mx-auto w-full">
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium">
            A decade of embedded product leadership
          </motion.p>

          <motion.h1 initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="text-[clamp(3rem,12vw,10rem)] font-light leading-[0.9] tracking-[-0.03em] mb-12">
            We deliver<br />
            the growth<br />
            you promised.
          </motion.h1>
          <span className="sr-only">FractionalCPO - Chief Product Officer services for growth-stage and PE-backed companies</span>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="flex flex-col sm:flex-row gap-6 items-start">
            <button onClick={() => { trackEvent('contact_click', { source: 'hero' }); setIsContactOpen(true); }} className="group relative px-8 py-4 bg-[#002178] text-white font-medium overflow-hidden transition-transform hover:scale-105">
              <span className="relative z-10">Let's chat</span>
            </button>
            <p className="text-sm text-black/50 max-w-xs leading-relaxed">
              AI-native product leaders who rebuild capability and accelerate revenue.
            </p>
          </motion.div>
          {/* Logos shown in stats carousel below — no TrustBar here */}
        </div>

        {/* Scroll indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1
      }} className="absolute bottom-12 left-1/2 -translate-x-1/2">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-black/20 to-black/40" />
        </motion.div>
      </section>

      {/* Stats Strip - Full Width */}
      <section className="bg-[#002178] pt-20 md:pt-32 pb-12 md:pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 mb-16">
            {stats.map((stat, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="text-center md:text-left">
                <p className="text-[clamp(3rem,8vw,6rem)] font-light text-white leading-none mb-2">
                  {stat.number}
                </p>
                <p className="text-white/90 font-medium mb-1">{stat.label}</p>
                <p className="text-white/50 text-sm">{stat.context}</p>
              </motion.div>)}
          </div>
        </div>
        <LogoCarousel variant="dark" />
      </section>

      {/* Problems - Editorial Grid */}
      <section className="py-24 md:py-40 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="md:sticky md:top-32">
              <p className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium">
                When to call us
              </p>
              <h2 className="text-[clamp(2.5rem,6vw,4.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8">
                Recognize<br />these signals?
              </h2>
              <button onClick={() => { trackEvent('contact_click', { source: 'problems_section' }); setIsContactOpen(true); }} className="px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform">
                Let's chat
              </button>
            </div>

            <div className="space-y-0">
              {problems.map((problem, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="group py-8 border-b border-black/10 cursor-pointer hover:border-[#002178] transition-colors">
                  <div className="flex gap-6 items-baseline">
                    <span className="text-[#002178]/30 font-mono text-sm">{problem.number}</span>
                    <div>
                      <h3 className="text-2xl md:text-3xl font-medium mb-2 group-hover:text-[#002178] transition-colors">
                        {problem.title}
                      </h3>
                      <p className="text-black/50">{problem.description}</p>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial - Minimal Inline with Polish */}
      <section className="py-16 md:py-24 bg-[#f5f5f0]">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          {/* Section heading */}
          <h2 className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium text-center">
            What Leaders Say
          </h2>

          <motion.div key={activeTestimonial} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.4
        }} className="relative pl-12 md:pl-16">
            {/* Teal quote mark - top left */}
            <span className="absolute top-0 left-0 text-[#0047FF] text-[5rem] md:text-[6rem] font-serif leading-none select-none pointer-events-none">"</span>

            <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-light leading-[1.5] mb-8 text-[#1a1a1a]/80 relative z-10">
              {testimonials[activeTestimonial].quote}
            </p>

            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-[#002178]">
                {testimonials[activeTestimonial].author}
              </span>
              <span className="text-black/30">&bull;</span>
              <span className="text-black/50">
                {testimonials[activeTestimonial].company}
              </span>
            </div>
          </motion.div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, i) => <button key={i} onClick={() => { trackEvent('testimonial_view', { index: String(i) }); setActiveTestimonial(i); }} className={`h-1.5 rounded-full transition-all duration-300 ${i === activeTestimonial ? "bg-[#002178] w-8" : "bg-black/15 w-1.5 hover:bg-black/30"}`} aria-label={`View testimonial ${i + 1}`} />)}
          </div>
        </div>
      </section>

      {/* Assessment CTA - Full Bleed */}
      <section className="bg-[#002178] py-24 md:py-40 relative overflow-hidden">
        {/* Abstract decoration */}
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 border border-white rounded-full" />
          <div className="absolute top-40 right-40 w-64 h-64 border border-white rounded-full" />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-white/50 text-sm uppercase tracking-[0.3em] mb-4">
                Product Assessment
              </p>
              <h2 className="text-[clamp(2.5rem,6vw,4rem)] font-light text-white leading-[1.1] mb-8">
                Expose drags<br />on your revenue
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-10 max-w-lg">
                In weeks, our team and proprietary AI audits your product growth levers, uncovering the 5% of work that drives all the impact.
              </p>
              <div className="flex flex-col gap-3 items-start">
                <button onClick={() => { trackEvent('contact_click', { source: 'assessment_cta' }); setIsContactOpen(true); }} className="px-8 py-4 bg-white text-[#002178] font-medium hover:bg-white/90 transition-colors">
                  Let's chat
                </button>
                <Link to="/assessment" className="text-sm text-white/50 hover:text-white underline underline-offset-4 transition-colors" onClick={() => trackEvent('internal_nav', { to: '/assessment', source: 'hero_link' })}>
                  Learn about our assessment &rarr;
                </Link>
              </div>
            </div>

            <div className="space-y-6">
              {["Product Growth", "Product Talent", "Product Marketing", "Product Strategy", "Operating System"].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="flex items-center gap-4 text-white/80 hover:text-white transition-colors cursor-default">
                  <div className="w-8 h-px bg-white/30" />
                  <span className="text-lg">{item}</span>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section id="who-we-are" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium">
                Who We Are
              </p>
              <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8">
                A decade of product leadership,{" "}
                <span className="text-[#002178] font-medium">embedded where it matters.</span>
              </h2>
              <p className="text-lg text-black/60 leading-relaxed mb-4">
                FractionalCPO is a firm of senior product leaders who take interim leadership roles inside your company. We don't advise from the sidelines. We embed, make decisions, ship product, and own outcomes.
              </p>
              <p className="text-lg text-black/60 leading-relaxed">
                Our team has led product at technology companies across enterprise platforms, marketplaces, and fintech. We bring battle-tested playbooks, not theory.
              </p>
            </div>
            <div className="space-y-6">
              {[
                { title: "Embedded, not advisory", description: "We join your team, attend your standups, and drive execution alongside your people." },
                { title: "Team, not solo contractor", description: "You get a team of senior product leaders with direct partner oversight on every engagement." },
                { title: "Outcomes, not hours", description: "We're accountable for measurable business impact, not time logged." },
              ].map((item, i) => (
                <div key={i} className="py-6 border-b border-black/10">
                  <h3 className="text-xl font-medium mb-2">{item.title}</h3>
                  <p className="text-black/50 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Slack Screenshots - Footer Reinforcement */}
      <section className="py-8 md:py-12 bg-[#f5f5f0] overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-4">
          <h2 className="text-sm uppercase tracking-[0.3em] text-[#002178] font-medium">
            Real feedback, unfiltered.
          </h2>
        </div>

        <div ref={slackScrollRef} className="flex items-center gap-6 px-6 overflow-x-auto" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
          {/* Triple the images for seamless infinite scroll */}
          {[0, 1, 2].map(setIndex => [{
          n: 1,
          h: 113
        }, {
          n: 2,
          h: 139
        }, {
          n: 3,
          h: 164
        }, {
          n: 4,
          h: 164
        }, {
          n: 5,
          h: 113
        }, {
          n: 6,
          h: 139
        }, {
          n: 7,
          h: 139
        }, {
          n: 8,
          h: 139
        }].map(({
          n,
          h
        }) => <img key={`${setIndex}-${n}`} src={`/slack/slack-${n}.png`} alt={slackAltTexts[n - 1]} width={420} height={h} className="flex-shrink-0 rounded-xl border border-[#E0E0E0]" loading="lazy" decoding="async" />))}
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSchema faqs={homepageFaqs} />

      {/* Footer */}
      <footer className="bg-[#002178] py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          {/* Top: Logo + tagline + Contact CTA */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
            <div className="flex items-center gap-3 mb-4 md:mb-0">
              <Link to="/">
                <img src={logo} alt="FractionalCPO" className="h-10" />
              </Link>
              <span className="text-white/50 text-sm">Revenue growth through product leadership.</span>
            </div>
            <button
              onClick={() => { trackEvent('contact_click', { source: 'footer' }); setIsContactOpen(true); }}
              className="px-6 py-2.5 bg-white text-[#002178] text-sm font-semibold hover:scale-105 transition-transform w-fit"
            >
              Contact Us
            </button>
          </div>

          {/* 4 columns */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div>
              <h4 className="font-semibold text-white text-xs uppercase tracking-wider mb-4">Work With Us</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/assessment" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/assessment' })}>The 5% Assessment</Link></li>
                <li><Link to="/ai" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/ai' })}>AI-Native Leadership</Link></li>
                <li><Link to="/pe" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/pe' })}>PE Firms</Link></li>
                <li><Link to="/case-study" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/case-study' })}>Case Study</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-xs uppercase tracking-wider mb-4">Learn</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/what-is-a-fractional-cpo" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/what-is-a-fractional-cpo' })}>What Is a FractionalCPO?</Link></li>
                <li><Link to="/fractional-cpo-cost" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/fractional-cpo-cost' })}>How much does a fractional CPO cost?</Link></li>
                <li><Link to="/product-led-growth" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/product-led-growth' })}>What is product growth?</Link></li>
                <li><Link to="/how-fractional-executives-work-together" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/how-fractional-executives-work-together' })}>How Fractional Execs Work Together</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-xs uppercase tracking-wider mb-4">Insights</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/fractional-cpo-vs-cmo" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/fractional-cpo-vs-cmo' })}>FractionalCPO vs CMO</Link></li>
                <li><Link to="/fractional-cpo-vs-cto" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/fractional-cpo-vs-cto' })}>FractionalCPO vs CTO</Link></li>
                <li><Link to="/fractional-cpo-vs-coo" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/fractional-cpo-vs-coo' })}>FractionalCPO vs COO</Link></li>
                <li><Link to="/fractional-cpo-vs-consultant" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/fractional-cpo-vs-consultant' })}>FractionalCPO vs Consultant</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-xs uppercase tracking-wider mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li><Link to="/about" className="hover:text-white transition-colors" onClick={() => trackEvent('footer_nav', { to: '/about' })}>About</Link></li>
                <li><button onClick={() => { trackEvent('contact_click', { source: 'footer' }); setIsContactOpen(true); }} className="hover:text-white transition-colors">Contact</button></li>
                <li><a href="https://www.linkedin.com/company/fractionalcpo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" onClick={() => trackEvent('outbound_click', { url: 'https://www.linkedin.com/company/fractionalcpo', label: 'linkedin' })}>LinkedIn</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between text-xs text-white/30 pt-6 border-t border-white/10">
            <p>&copy; {new Date().getFullYear()} FractionalCPO. All rights reserved.</p>
            <div className="flex items-center gap-4 mt-2 md:mt-0">
              <Link to="/privacy" className="hover:text-white/50 transition-colors" onClick={() => trackEvent('footer_nav', { to: '/privacy' })}>Privacy</Link>
              <Link to="/terms" className="hover:text-white/50 transition-colors" onClick={() => trackEvent('footer_nav', { to: '/terms' })}>Terms</Link>
              <button type="button" className="hover:text-white/50 transition-colors" onClick={() => CookieConsent.showPreferences()}>Cookie preferences</button>
            </div>
          </div>
        </div>
      </footer>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </div>;
};
export default LandingV2;
